import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  LowStockRow,
  MenuEngineeringRow,
  ReportFilter,
  SalesByBranchRow,
  SalesByCashierRow,
  SalesByCategoryRow,
  SalesByDayRow,
  SalesByHourRow,
  SalesByItemRow,
  SalesByPaymentMethodRow,
  WastageByItemRow,
} from './types';

// TanStack Query hooks for each report. Stale time = 60s so the operator
// can flip between reports without re-firing every query, but a fresh
// re-mount picks up new sales fast enough.

function buildParams(filter: ReportFilter): Record<string, string> {
  const params: Record<string, string> = {
    fromUtc: filter.fromUtc,
    toUtc: filter.toUtc,
  };
  if (filter.branchId) params['branchId'] = filter.branchId;
  return params;
}

const STALE_MS = 60_000;

export function useSalesByDay(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-day', filter],
    queryFn: async () =>
      (await apiClient.get<SalesByDayRow[]>('/api/reports/sales-by-day', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

export function useSalesByItem(filter: ReportFilter, limit = 25, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-item', filter, limit],
    queryFn: async () =>
      (await apiClient.get<SalesByItemRow[]>('/api/reports/sales-by-item', {
        params: { ...buildParams(filter), limit },
      })).data,
    staleTime: STALE_MS,
  });
}

export function useSalesByCategory(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-category', filter],
    queryFn: async () =>
      (await apiClient.get<SalesByCategoryRow[]>('/api/reports/sales-by-category', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

export function useSalesByCashier(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-cashier', filter],
    queryFn: async () =>
      (await apiClient.get<SalesByCashierRow[]>('/api/reports/sales-by-cashier', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

export function useSalesByHour(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-hour', filter],
    queryFn: async () =>
      (await apiClient.get<SalesByHourRow[]>('/api/reports/sales-by-hour', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

export function useSalesByPaymentMethod(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'sales-by-payment-method', filter],
    queryFn: async () =>
      (await apiClient.get<SalesByPaymentMethodRow[]>('/api/reports/sales-by-payment-method', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

/**
 * Open the Excel export endpoint in a new tab. Browser drives the
 * download; we don't fetch the bytes ourselves because TanStack Query
 * is a poor fit for file downloads.
 *
 * The Authorization header gets attached to all our fetch() calls but
 * the browser's "open in new tab" can't ride a bearer token, so we
 * fetch and trigger a download with a temporary anchor.
 */
export async function downloadReportExcel(
  reportSlug: string,
  filter: ReportFilter,
  extraParams: Record<string, string> = {},
): Promise<void> {
  const response = await apiClient.get<Blob>(`/api/reports/${reportSlug}/excel`, {
    params: { ...buildParams(filter), ...extraParams },
    responseType: 'blob',
  });

  const blob = response.data;
  const filename =
    extractFilename(response.headers['content-disposition']) ?? `${reportSlug}.xlsx`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function extractFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  // RFC 5987 form: filename*=UTF-8''sales-by-day.xlsx
  const star = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plain?.[1] ?? null;
}

// ---- Phase 6-Reports: multi-branch consolidation + menu engineering ----

export function useSalesByBranch(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    // Branch-rollup is tenant-wide by definition; we drop branchId from
    // the key so flipping the page-level branch filter doesn't refetch.
    queryKey: ['reports', 'sales-by-branch', filter.fromUtc, filter.toUtc],
    queryFn: async () =>
      (await apiClient.get<SalesByBranchRow[]>('/api/reports/sales-by-branch', {
        params: { fromUtc: filter.fromUtc, toUtc: filter.toUtc },
      })).data,
    staleTime: STALE_MS,
  });
}

export function useMenuEngineering(filter: ReportFilter, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'menu-engineering', filter],
    queryFn: async () =>
      (await apiClient.get<MenuEngineeringRow[]>('/api/reports/menu-engineering', {
        params: buildParams(filter),
      })).data,
    staleTime: STALE_MS,
  });
}

// ---- Inventory reports (Phase 2E) ----

/**
 * Variant of downloadReportExcel that takes raw query params instead of
 * a ReportFilter — for reports that aren't date-range scoped (low stock
 * takes branchId + threshold only).
 */
export async function downloadInventoryExcel(
  reportSlug: string,
  params: Record<string, string>,
): Promise<void> {
  const response = await apiClient.get<Blob>(`/api/reports/${reportSlug}/excel`, {
    params,
    responseType: 'blob',
  });
  const blob = response.data;
  const filename =
    extractFilename(response.headers['content-disposition']) ?? `${reportSlug}.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function useLowStock(branchId: string | null, threshold: number, enabled = true) {
  return useQuery({
    enabled: enabled && !!branchId,
    queryKey: ['reports', 'low-stock', branchId, threshold],
    queryFn: async () =>
      (await apiClient.get<LowStockRow[]>('/api/reports/low-stock', {
        params: { branchId, threshold },
      })).data,
    staleTime: STALE_MS,
  });
}

export function useWastageByItem(
  branchId: string | null,
  filter: ReportFilter,
  enabled = true,
) {
  return useQuery({
    enabled: enabled && !!branchId,
    queryKey: ['reports', 'wastage-by-item', branchId, filter],
    queryFn: async () =>
      (await apiClient.get<WastageByItemRow[]>('/api/reports/wastage-by-item', {
        params: {
          branchId,
          fromUtc: filter.fromUtc,
          toUtc: filter.toUtc,
        },
      })).data,
    staleTime: STALE_MS,
  });
}
